// Columna derecha del explorer: la ficha de una evaluación — hero con SCI, nivel y decisión, y dos
// pestañas (Panel de control · Metadata). Presentacional: el estado lo pone ModeliaPage.
import type { ReactNode } from 'react';
import { Sun } from 'lucide-react';
import type { SciEvaluationResponse } from '@fiado/type-kit/bin/loanScoring/dtos/responses/SciEvaluationResponse';
import { CopyableId } from '@/kit';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail/MasterDetail';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import {
    componentLabel,
    explainContradiction,
    LAYER2_SOURCE_LABEL,
    NO_VALUE,
    SCI_SCALE_LABEL,
    SCORING_PROVIDER_LABEL,
} from '../logic';
import { DecisionPill, LevelPill } from './ModeliaPills';

export const MODELIA_DETAIL_TABS = ['panel', 'metadata'] as const;
export type ModeliaDetailTab = (typeof MODELIA_DETAIL_TABS)[number];

const TAB_LABEL: Record<ModeliaDetailTab, string> = {
    panel: 'Panel de control',
    metadata: 'Metadata',
};

const KEY_LABEL = 'text-[9px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

function Card({
    title,
    meta,
    children,
    tone = 'default',
}: {
    title: string;
    meta?: string;
    children: ReactNode;
    tone?: 'default' | 'danger';
}) {
    return (
        <section
            className={cn(
                'mb-3 overflow-hidden rounded-md border bg-card',
                tone === 'danger' ? 'border-destructive/40' : 'border-border',
            )}
        >
            <header
                className={cn(
                    'flex items-center justify-between gap-2 border-b px-3 py-2',
                    tone === 'danger'
                        ? 'border-destructive/30 bg-destructive/10'
                        : 'border-border bg-muted/30',
                )}
            >
                <span
                    className={cn(
                        'text-[10.5px] font-bold uppercase tracking-[0.04em]',
                        tone === 'danger' ? 'text-destructive' : 'text-muted-foreground',
                    )}
                >
                    {title}
                </span>
                {meta ? <span className="text-[10.5px] text-muted-foreground">{meta}</span> : null}
            </header>
            <div className="p-3">{children}</div>
        </section>
    );
}

function Field({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
    return (
        <div className="min-w-0">
            <div className={KEY_LABEL}>{label}</div>
            <div className={cn('break-words text-[12px]', mono && 'font-mono text-[11px]')}>
                {value}
            </div>
        </div>
    );
}

/** Los sub-scores de una capa. Sin `components` la tarjeta lo dice; nunca pinta barras vacías. */
function ComponentList({ components }: { components: Record<string, number> | undefined }) {
    const entries = Object.entries(components ?? {});
    if (entries.length === 0) {
        return (
            <p className="mt-2 text-[11px] text-muted-foreground">
                Esta capa llegó sin el detalle por variable.
            </p>
        );
    }
    return (
        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
            {entries.map(([key, value]) => (
                <div key={key} className="flex items-baseline justify-between gap-2">
                    <dt className="truncate text-[11px] text-muted-foreground">
                        {componentLabel(key)}
                    </dt>
                    <dd className="flex-none text-[11px] font-semibold tabular-nums">{value}</dd>
                </div>
            ))}
        </dl>
    );
}

function OfferCard({ evaluation }: { evaluation: SciEvaluationResponse }) {
    const { offer } = evaluation;
    if (offer === null) {
        return (
            <Card title="Oferta">
                <p className="text-[11.5px] text-muted-foreground">
                    El proveedor no entregó oferta para esta evaluación.
                </p>
            </Card>
        );
    }
    const rates = Object.entries(offer.rates);
    return (
        <Card title="Oferta" meta="valores tal como los entregó el proveedor">
            <div className="grid grid-cols-3 gap-3">
                <Field
                    label="Línea de crédito"
                    value={offer.creditLine === null ? NO_VALUE : offer.creditLine}
                />
                <Field
                    label="Enganche"
                    value={offer.downPayment === null ? NO_VALUE : offer.downPayment}
                />
                <Field
                    label="Plazos"
                    value={
                        offer.termsWeeks.length === 0
                            ? NO_VALUE
                            : `${offer.termsWeeks.join(', ')} semanas`
                    }
                />
            </div>
            <div className="mt-3">
                <div className={KEY_LABEL}>Tasas por plazo</div>
                <div className="text-[12px]">
                    {rates.length === 0
                        ? NO_VALUE
                        : rates.map(([term, rate]) => `${term} sem: ${String(rate)}`).join(' · ')}
                </div>
            </div>
            {/* El contrato de Modelias no confirma unidades: convertir a pesos sería inventar la escala. */}
            <p className="mt-3 text-[10.5px] text-muted-foreground">
                Las cifras van sin convertir: el contrato del proveedor no declara sus unidades.
            </p>
        </Card>
    );
}

function BreakdownCard({ evaluation }: { evaluation: SciEvaluationResponse }) {
    const { layer1, layer2, layer3 } = evaluation.breakdown;
    return (
        <Card title="Desglose por capas">
            <div className="space-y-3">
                <div>
                    <div className="flex items-baseline justify-between gap-2">
                        <span className="text-[11.5px] font-semibold">
                            Capa 1 · gate anti-fraude
                        </span>
                        <span
                            className={cn(
                                'text-[11px] font-bold',
                                layer1.passed ? 'text-success' : 'text-destructive',
                            )}
                        >
                            {layer1.passed ? 'Pasó' : 'No pasó'}
                        </span>
                    </div>
                </div>

                {layer2 === undefined ? null : (
                    <div className="border-t border-border/60 pt-2">
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="text-[11.5px] font-semibold">Capa 2 · buró</span>
                            <span className="text-[11px] font-bold tabular-nums">
                                {layer2.score}
                            </span>
                        </div>
                        <div className="text-[10.5px] text-muted-foreground">
                            {LAYER2_SOURCE_LABEL[layer2.source]}
                        </div>
                        <ComponentList components={layer2.components} />
                    </div>
                )}

                {layer3 === undefined ? null : (
                    <div className="border-t border-border/60 pt-2">
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="text-[11.5px] font-semibold">
                                Capa 3 · historial interno
                            </span>
                            <span className="text-[11px] font-bold tabular-nums">
                                {layer3.score}
                            </span>
                        </div>
                        <ComponentList components={layer3.components} />
                    </div>
                )}

                {layer2 === undefined && layer3 === undefined ? (
                    <p className="border-t border-border/60 pt-2 text-[11.5px] text-muted-foreground">
                        El proveedor no entregó desglose por capas: la decisión llegó completa y no
                        hay SCI calculado por capas para esta evaluación.
                    </p>
                ) : null}
            </div>
        </Card>
    );
}

export interface ModeliaEvaluationDetailProps {
    /** `null` pinta el estado vacío de la columna. */
    row: { evaluation: SciEvaluationResponse; customerName: string | null } | null;
    tab: ModeliaDetailTab;
    onTabChange: (tab: ModeliaDetailTab) => void;
    /** Acciones sobre la evaluación abierta (hoy, volver a correr el análisis). */
    actions?: ReactNode;
}

export function ModeliaEvaluationDetail({
    row,
    tab,
    onTabChange,
    actions,
}: ModeliaEvaluationDetailProps) {
    if (!row) {
        return (
            <MasterDetailEmpty
                icon={<Sun className="size-10" aria-hidden />}
                title="Selecciona una evaluación"
                sub="Elige una evaluación de la lista para ver su SCI, su desglose y su oferta."
            />
        );
    }

    const { evaluation, customerName } = row;
    const contradiction = explainContradiction(evaluation);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex-none border-b border-border bg-card px-4 py-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <h2 className="text-[15px] font-bold leading-tight">
                        {customerName ?? 'Acreditado sin nombre en el padrón'}
                    </h2>
                    <CopyableId label="Evaluación" value={evaluation.evaluationId} />
                </div>

                {actions === undefined ? null : <div className="mt-2">{actions}</div>}

                <div className="mt-2.5 grid grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] gap-3">
                    <div className="min-w-0">
                        <div className={KEY_LABEL}>SCI</div>
                        <div className="text-[26px] font-bold leading-none tabular-nums">
                            {evaluation.sciTotal}
                        </div>
                        <div className="mt-0.5 text-[10px] text-muted-foreground">
                            {SCI_SCALE_LABEL}
                        </div>
                    </div>
                    <div className="min-w-0">
                        <div className={KEY_LABEL}>Nivel</div>
                        <div className="mt-1">
                            <LevelPill level={evaluation.level} size="md" />
                        </div>
                        <div className="mt-1 text-[10px] text-muted-foreground">
                            Se deriva del SCI
                        </div>
                    </div>
                    <div className="min-w-0">
                        <div className={KEY_LABEL}>Decisión</div>
                        <div className="mt-1">
                            <DecisionPill decision={evaluation.decision} size="md" />
                        </div>
                        <div className="mt-1 text-[10px] text-muted-foreground">
                            {SCORING_PROVIDER_LABEL[evaluation.provider]}
                        </div>
                    </div>
                </div>
            </div>

            <div
                className="flex flex-none gap-1 border-b border-border bg-card px-4"
                role="tablist"
            >
                {MODELIA_DETAIL_TABS.map((key) => {
                    const active = key === tab;
                    return (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => {
                                onTabChange(key);
                            }}
                            className={cn(
                                'my-1.5 rounded-md px-2.5 py-1 text-[11.5px] transition-colors',
                                active
                                    ? 'bg-accent font-semibold text-foreground'
                                    : 'font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                            )}
                        >
                            {TAB_LABEL[key]}
                        </button>
                    );
                })}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-muted/15 px-4 py-3">
                {contradiction ? (
                    <Card title="Evaluación inconsistente" tone="danger">
                        <p role="alert" className="text-[11.5px] leading-relaxed">
                            {contradiction}
                        </p>
                        <div className="mt-2.5 grid grid-cols-3 gap-3">
                            <Field label="decision" value={evaluation.decision} mono />
                            <Field label="level" value={evaluation.level} mono />
                            <Field label="sciTotal" value={evaluation.sciTotal} mono />
                        </div>
                        {evaluation.offer === null ? null : (
                            <p className="mt-2.5 text-[11px] text-destructive">
                                Además trae una oferta calculada encima de esa decisión.
                            </p>
                        )}
                    </Card>
                ) : null}

                {tab === 'panel' ? (
                    <>
                        <OfferCard evaluation={evaluation} />
                        <BreakdownCard evaluation={evaluation} />
                    </>
                ) : (
                    <Card title="Metadata del snapshot" meta="LoanSciEvaluation_GT">
                        <div className="grid grid-cols-2 gap-3">
                            <CopyableId label="evaluationId" value={evaluation.evaluationId} />
                            <CopyableId label="borrowerId" value={evaluation.borrowerId} />
                            <Field
                                label="Proveedor"
                                value={SCORING_PROVIDER_LABEL[evaluation.provider]}
                            />
                            <Field label="Versión" value={evaluation.version} />
                            <Field label="Thin file" value={evaluation.thinFile ? 'Sí' : 'No'} />
                            <Field
                                label="Creada"
                                value={`${formatDateTimeMx(evaluation.createdAt)} · ${evaluation.createdBy}`}
                            />
                            <Field
                                label="Actualizada"
                                value={`${formatDateTimeMx(evaluation.updatedAt)} · ${evaluation.updatedBy}`}
                            />
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
}
